import { Injectable } from "@angular/core";

/**
 * Remembers an MCP consent URL across the login redirect so an unauthenticated
 * authorize request can be resumed once the user signs in. Scoped strictly to
 * the consent route — no other URL is ever stored or returned.
 */
@Injectable({ providedIn: "root" })
export class McpReturnUrlService {
  private static readonly STORAGE_KEY = "mcpConsentReturnUrl";
  private static readonly CONSENT_PATH = "/mcp/consent";

  /** Stores a consent URL so the user can be returned to it after logging in. */
  store(url: string): void {
    if (!this.isConsentUrl(url)) {
      return;
    }
    try {
      sessionStorage.setItem(McpReturnUrlService.STORAGE_KEY, url);
    } catch {
      // sessionStorage may be unavailable (private browsing); the return URL is simply not kept.
    }
  }

  /** Returns and clears a stored consent URL, or null when there is none. */
  consume(): string | null {
    try {
      const url = sessionStorage.getItem(McpReturnUrlService.STORAGE_KEY);
      sessionStorage.removeItem(McpReturnUrlService.STORAGE_KEY);
      return this.isConsentUrl(url) ? url : null;
    } catch {
      // sessionStorage may be unavailable; treat as no stored URL.
      return null;
    }
  }

  private isConsentUrl(url: string | null): boolean {
    return !!url && url.includes(McpReturnUrlService.CONSENT_PATH);
  }
}
